import React from "react";
import { useSelector } from "react-redux";
import { useI18n } from "i18n";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Checkbox } from "@remo-co/ui-core/src/components/Checkbox";
import { FormControlLabel } from "@remo-co/ui-core/src/components/FormControlLabel";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Delete } from "@remo-co/ui-core/src/icons/Delete";
import { selectMyCompany } from "modules/company/redux/selectors";
import { selectUser } from "modules/auth/redux/selectors";
import useCompanySettingsActions from "modules/companySettings/hooks/useCompanySettingsActions";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { updateCompanySettings } from "services/apiService/apis";
import * as FileStoragePaths from "services/firebaseService/storagePaths";
import UploadCaption, { BestUploadSizes } from "modules/uploader/UploadCaption";
import { ImageUploader } from "modules/uploader";
import Avatar from "modules/userAvatar/Avatar";
import { ICompanySettings } from "modules/company/types";
import {
  selectCompanyBadgeUrl,
  selectEnableCompanyBadge,
} from "modules/companySettings/redux/selectors";
import { CheckedIcon } from "@remo-co/ui-core/src/icons/Checked";
import BadgeBackgroundIcon from "../assets/badge-background.inline.png";
import BadgePreview from "./BadgePreview";
import { useStyles, styles } from "./styles";

/*
 * This page will show company badge feature to company owner so he can add company badge on company members logo
 * CompanyOwner come and upload or change company badge anytime for his company members.
 */

const CompanyBadge = (): JSX.Element | null => {
  const company = useSelector(selectMyCompany);
  const { setCompanySettings } = useCompanySettingsActions();
  const { t } = useI18n(["common", "settings", "server"]);
  const user = useSelector(selectUser);
  const { addErrorNotification, addSuccessNotification } =
    useNotificationActions();
  const companyBadgeURL = useSelector(selectCompanyBadgeUrl);
  const enableCompanyBadge = useSelector(selectEnableCompanyBadge);
  const [badgeUrl, setBadgeUrl] = React.useState("");
  const [enableBadge, setEnableCompanyBadge] = React.useState(false);
  const classes = useStyles();

  React.useEffect(() => {
    setBadgeUrl(companyBadgeURL);
    setEnableCompanyBadge(enableCompanyBadge);
  }, [enableCompanyBadge, companyBadgeURL]);

  const handleEnableBadgeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEnableCompanyBadge(e.target.checked);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    saveChanges({ enableCompanyBadge: e.target.checked });
  };

  const handleBadgeUrlChange = (imageURL: string) => {
    setBadgeUrl(imageURL);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    saveChanges({ companyBadgeURL: imageURL });
  };

  const handleDelete = () => {
    setBadgeUrl("");
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    saveChanges({ companyBadgeURL: "" });
  };

  const saveChanges = async (companySettings: Partial<ICompanySettings>) => {
    if (company && company.id) {
      const res = await updateCompanySettings(company.id, {
        ...companySettings,
        company: company.id,
      });

      if (!res.isSuccess) {
        addErrorNotification({
          message: t("failed.save.company.badge"),
          position: "tc",
          autoDismiss: 5,
        });

        return;
      }

      if (res.companySettings) {
        setCompanySettings(res.companySettings);
      }
      addSuccessNotification({
        message: t("success.save.company.badge"),
        position: "tr",
        autoDismiss: 5,
      });
    }
  };

  return (
    <Box className={classes.container}>
      <Typography variant="h3" color="textPrimary">
        {t("settings:company.badge")}
      </Typography>
      <Typography color="textPrimary" sx={styles.description}>
        {t("settings:company.badge.description")}
      </Typography>

      <FormControlLabel
        className="mar-bottom-10"
        control={
          <Checkbox
            data-testid="enable-badge"
            color="primary"
            checked={enableBadge}
            onChange={handleEnableBadgeChange}
            icon={<Typography sx={styles.checkbox} />}
            checkedIcon={
              <Typography sx={styles.checkboxIcon}>
                <CheckedIcon />
              </Typography>
            }
          />
        }
        label={t("settings:enable.company.badge")}
      />

      <div className={classes.imageContainer}>
        <div className={classes.imageForm}>
          <BadgePreview badgeUrl={badgeUrl} size="md" isCompanyBadgeEdit />
          <div className={classes.imageDetails}>
            <ImageUploader
              name="companyBadge"
              level={FileStoragePaths.UPLOAD_LEVEL.USER}
              path={FileStoragePaths.COMPANY_BADGE_IMAGE}
              onImageUploaded={handleBadgeUrlChange}
              data-testid="company-badge-url"
              variant="secondary"
              color="blue"
              size="md"
            >
              {badgeUrl
                ? t("settings:change.image")
                : t("settings:upload.image")}
            </ImageUploader>
          </div>
          {badgeUrl && (
            <Button
              data-testid="delete-button"
              color="dark"
              variant="warning"
              size="md"
              onClick={handleDelete}
              sx={styles.deleteButton}
            >
              <Delete />
            </Button>
          )}
          <div className={classes.badgeBackground}>
            <img
              src={BadgeBackgroundIcon}
              alt="badge-background"
              className={classes.badgeBackgroundImage}
            />
            <Avatar
              className={classes.badgePreviewAvatar}
              user={user}
              isCompanyBadgeEdit
            />
          </div>
        </div>
        <UploadCaption bestSize={BestUploadSizes.badge} allowGif />
      </div>
    </Box>
  );
};

export default CompanyBadge;
